import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DEMO_URL } from '../../../data/links';
import StartChooser from '../../../components/StartChooser';

const DASHBOARD = '/images/home/hero-dashboard.webp';

/**
 * Home hero: one light surface that matches the rest of the site. Copy on the left, a single
 * flat product shot on the right, and nothing else competing for attention.
 */
export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative isolate overflow-hidden bg-white" aria-labelledby="home-hero-title">
      <HomeHeroBackdrop />

      {/* Announcement bar, directly under the fixed navbar */}
      <div className="relative mt-[60px] border-y border-blue-100/80 bg-blue-50/60">
        <Link
          to="/pricing"
          className="group mx-auto flex max-w-7xl items-center justify-center gap-2 px-5 py-2 text-center text-xs text-slate-700 transition-colors hover:text-slate-950 sm:text-[13px]"
        >
          <span className="flex-none rounded-full bg-[#1769ff] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">{t('home.hero.announceBadge')}</span>
          <span className="min-w-0 truncate">{t('home.hero.announceText')}</span>
          <span className="hidden flex-none items-center gap-1 font-semibold text-[#1769ff] sm:inline-flex">
            {t('home.hero.announceCta')}
            <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5 rtl:rotate-180" aria-hidden />
          </span>
        </Link>
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-16 pt-12 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.08fr)] lg:gap-14 lg:pb-24 lg:pt-20">
        {/* Copy */}
        <div className="text-center lg:text-start">
          <p className="inline-flex items-center gap-2 rounded-full border border-blue-200/80 bg-white px-3 py-1 text-xs font-semibold text-[#1769ff] shadow-sm opacity-0 animate-fade-in-up">
            <i className="ri-sparkling-2-line text-sm" aria-hidden />
            {t('home.hero.badge')}
          </p>

          <h1
            id="home-hero-title"
            className="mt-5 text-[2.35rem] font-bold leading-[1.06] tracking-[-0.035em] text-slate-950 opacity-0 animate-fade-in-up animation-delay-100 min-[420px]:text-[2.6rem] sm:text-5xl lg:text-[3.5rem]"
          >
            <span className="block [text-wrap:balance]">{t('home.hero.title')}</span>
            <span className="block text-[#1769ff]">{t('home.hero.titleEnd')}</span>
          </h1>

          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-600 opacity-0 animate-fade-in-up animation-delay-200 sm:text-lg lg:mx-0">
            {t('home.hero.description')}
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 opacity-0 animate-fade-in-up animation-delay-300 sm:flex-row sm:justify-center lg:justify-start">
            <StartChooser
              label={t('home.hero.ctaPrimary')}
              className="inline-flex h-12 w-full items-center justify-center rounded-lg bg-[#1769ff] px-7 text-[15px] font-semibold text-white transition-colors hover:bg-[#0f55d4] sm:w-auto"
            />
            <a
              href={DEMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-12 w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-6 text-[15px] font-semibold text-slate-800 transition-colors hover:border-slate-400 hover:bg-slate-50 sm:w-auto"
            >
              {t('home.hero.ctaSecondary')}
            </a>
          </div>
          <p className="mt-4 text-[13px] text-slate-500 opacity-0 animate-fade-in-up animation-delay-300">{t('home.hero.microTrust')}</p>

          {/* Proof: verified claims only */}
          <ul className="m-0 mt-7 flex list-none flex-col items-center gap-2.5 border-t border-slate-200/80 p-0 pt-6 text-sm text-slate-700 opacity-0 animate-fade-in-up animation-delay-400 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-6 lg:justify-start">
            <li className="text-center lg:text-start">
              <i className="ri-line-chart-line me-1.5 align-[-2px] text-emerald-600" aria-hidden />
              <strong className="whitespace-nowrap font-semibold text-slate-950">{t('home.hero.statCostValue')}</strong>{' '}
              {t('home.hero.costLine')}
            </li>
            <li className="text-center lg:text-start">
              <i className="ri-shield-check-line me-1.5 align-[-2px] text-[#1769ff]" aria-hidden />
              {t('home.hero.proofSecurity')}
            </li>
          </ul>
        </div>

        {/* Product */}
        <div className="relative opacity-0 animate-fade-in-up animation-delay-300">
          <div className="pointer-events-none absolute -inset-6 -z-10 rounded-[36px] bg-gradient-to-br from-blue-100/70 via-sky-50/60 to-indigo-100/50 blur-2xl" aria-hidden />
          <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_30px_70px_-30px_rgba(15,40,90,0.35)]">
            <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-2.5">
              <span className="flex gap-1.5" aria-hidden>
                <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              </span>
              <span className="mx-auto hidden items-center gap-1.5 rounded-md bg-white px-3 py-0.5 text-[11px] text-slate-500 ring-1 ring-slate-200 sm:flex">
                <i className="ri-lock-2-fill text-[10px] text-slate-400" aria-hidden />
                app.geofleet.ai/command-centre
              </span>
              <span className="ms-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 sm:ms-0">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t('home.hero.feedLive')}
              </span>
            </div>
            <img
              src={DASHBOARD}
              alt={t('home.hero.imageAlt')}
              width={1586}
              height={992}
              loading="eager"
              fetchPriority="high"
              className="block w-full"
            />
          </div>

          {/* One agent event, to show the AI at work */}
          <div className="absolute -bottom-6 start-4 hidden max-w-[300px] items-center gap-3 rounded-2xl border border-slate-200 bg-white/95 px-4 py-3 shadow-[0_18px_40px_-18px_rgba(15,40,90,0.4)] backdrop-blur sm:flex lg:-start-8">
            <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#1769ff] text-white">
              <i className="ri-sparkling-2-fill text-base" aria-hidden />
            </span>
            <span>
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-[#1769ff]">{t('home.hero.chipCopilot')}</span>
              <span className="block text-[13px] font-semibold leading-snug text-slate-900">{t('home.hero.chipCopilotText')}</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Soft light wash with a faint grid that fades out, so the hero reads as part of the page. */
function HomeHeroBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" aria-hidden>
      <div className="absolute inset-0 bg-gradient-to-b from-[#f3f7ff] via-white to-white" />
      <div className="absolute inset-0 bg-[radial-gradient(50%_60%_at_85%_20%,rgba(23,105,255,0.10),transparent_70%)]" />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(23,105,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(23,105,255,0.05) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 80% 70% at 50% 0%, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 70% at 50% 0%, black 30%, transparent 80%)',
        }}
      />
    </div>
  );
}
